// Runtime config (ADR-074, port-map S08-086): the console build is the same for every environment; the edge
// Worker's console route (or the build's generated runtime-config.json) says which API, Supabase project and
// analytics it talks to. Nothing environment-specific is compiled in.

export type Environment = "mock" | "local" | "preview" | "staging" | "production";

export interface RuntimeConfig {
  environment: Environment;
  /** The commit this build came from, also served as the nodus-release meta tag. */
  release: string;
  /** `https://api.<domain>`; empty in mock mode (same-origin MSW). */
  apiURL: string;
  inferenceURL?: string;
  supabaseURL?: string;
  supabaseAnonKey?: string;
  docsURL: string;
  /** Where customers reach Nodus support; the site's support mailbox stands in when it is unset. */
  supportURL?: string;
  changelogURL?: string;
  cookieDomain?: string;
  turnstileSiteKey?: string;
  posthogKey?: string;
  posthogHost?: string;
}

const MOCK: RuntimeConfig = {
  environment: "mock",
  release: "dev",
  apiURL: "",
  docsURL: "https://nodus.example.com/docs",
  changelogURL: "https://nodus.example.com/changelog",
};

function valid(c: Partial<RuntimeConfig>): c is RuntimeConfig {
  if (!c.environment || !c.docsURL || c.release === undefined || c.apiURL === undefined) return false;
  if (c.environment !== "mock" && (!c.apiURL || !c.supabaseURL || !c.supabaseAnonKey)) return false;
  return true;
}

/** Explicit JSON configuration fails closed; only an absent development config starts mocks. */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const res = await fetch("/runtime-config.json", { cache: "no-store" });
  if (res.ok && (res.headers.get("Content-Type") ?? "").includes("json")) {
    const c = (await res.json()) as Partial<RuntimeConfig>;
    if (valid(c)) return c;
    throw new Error("runtime-config.json is incomplete");
  }
  if (!import.meta.env.DEV || (!res.ok && res.status !== 404)) throw new Error("runtime-config.json is unavailable");
  return MOCK;
}
